'use client'

import * as React from 'react'
import { IconX } from '@tabler/icons-react'
import { cn } from '../utils/cn'

export interface EmailTag {
  id: string
  email: string
}

type InputEmailTagsProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> & {
  value: string[]
  onChange: (value: string[]) => void
  /** Validate email before adding. Defaults to basic email validation */
  validateEmail?: (email: string) => boolean
  /** Called when an invalid email is attempted to be added */
  onInvalidEmail?: (email: string) => void
  /** Whether the input is invalid (shows error styling) */
  invalid?: boolean
  /** Minimum number of rows to display. Defaults to 3 */
  minRows?: number
}

const defaultEmailValidator = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

interface EditableTagProps {
  email: string
  index: number
  isEditing: boolean
  disabled?: boolean
  onStartEdit: (index: number) => void
  onFinishEdit: (index: number, newValue: string) => void
  onCancelEdit: () => void
  onRemove: (email: string) => void
}

const EditableTag = ({
  email,
  index,
  isEditing,
  disabled,
  onStartEdit,
  onFinishEdit,
  onCancelEdit,
  onRemove,
}: EditableTagProps) => {
  const [editValue, setEditValue] = React.useState(email)
  const editInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (isEditing) {
      setEditValue(email)
      // Focus and place cursor at the end when entering edit mode
      setTimeout(() => {
        const input = editInputRef.current
        if (input) {
          input.focus()
          input.setSelectionRange(input.value.length, input.value.length)
        }
      }, 0)
    }
  }, [isEditing, email])

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        onFinishEdit(index, editValue)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onCancelEdit()
      }
    },
    [editValue, index, onFinishEdit, onCancelEdit],
  )

  const handleBlur = React.useCallback(() => {
    onFinishEdit(index, editValue)
  }, [editValue, index, onFinishEdit])

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-[10px] rounded-[6px] bg-violet-200 px-2 py-1 shrink-0 transition-colors',
        !isEditing && 'cursor-pointer hover:bg-violet-300',
      )}
      onClick={(e) => {
        e.stopPropagation()
        if (!disabled && !isEditing) {
          onStartEdit(index)
        }
      }}
      role='button'
      tabIndex={disabled || isEditing ? -1 : 0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled && !isEditing) {
          e.preventDefault()
          onStartEdit(index)
        }
      }}
    >
      <div className='relative'>
        {/* Hidden span to maintain consistent width */}
        <span
          className='font-normal text-[12px] text-slate-700 tracking-[-0.36px] leading-5 whitespace-pre'
          style={{ visibility: isEditing ? 'hidden' : 'visible' }}
        >
          {isEditing ? editValue || ' ' : email}
        </span>
        {isEditing && (
          <input
            ref={editInputRef}
            type='text'
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className='absolute inset-0 bg-transparent outline-none font-normal text-[12px] text-slate-700 tracking-[-0.36px] leading-5 w-full'
            disabled={disabled}
          />
        )}
      </div>
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation()
          onRemove(email)
        }}
        disabled={disabled}
        className={cn(
          'size-[14px] flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors disabled:cursor-not-allowed',
          isEditing && 'opacity-0 pointer-events-none',
        )}
        tabIndex={isEditing ? -1 : 0}
        aria-label={`Eliminar ${email}`}
      >
        <IconX className='size-[14px]' />
      </button>
    </div>
  )
}

const InputEmailTags = React.forwardRef<HTMLInputElement, InputEmailTagsProps>(
  (
    {
      className,
      value = [],
      onChange,
      validateEmail = defaultEmailValidator,
      onInvalidEmail,
      invalid,
      placeholder = 'Ejemplo: email@ejemplo.com',
      disabled,
      minRows = 3,
      ...props
    },
    ref,
  ) => {
    const [pendingInput, setPendingInput] = React.useState('')
    const [editingIndex, setEditingIndex] = React.useState<number | null>(null)
    const inputRef = React.useRef<HTMLInputElement>(null)

    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current!)

    const addEmail = React.useCallback(
      (email: string) => {
        const trimmed = email.trim().toLowerCase()
        if (!trimmed) return

        if (!validateEmail(trimmed)) {
          onInvalidEmail?.(trimmed)
          return
        }

        // Avoid duplicates
        if (value.includes(trimmed)) {
          setPendingInput('')
          return
        }

        onChange([...value, trimmed])
        setPendingInput('')
      },
      [value, onChange, validateEmail, onInvalidEmail],
    )

    const removeEmail = React.useCallback(
      (emailToRemove: string) => {
        onChange(value.filter((email) => email !== emailToRemove))
      },
      [value, onChange],
    )

    const updateEmail = React.useCallback(
      (index: number, newEmail: string) => {
        const trimmed = newEmail.trim().toLowerCase()

        // If empty, remove the tag
        if (!trimmed) {
          onChange(value.filter((_, i) => i !== index))
          setEditingIndex(null)
          return
        }

        // If unchanged, just close edit mode
        if (trimmed === value[index]) {
          setEditingIndex(null)
          return
        }

        // Validate email
        if (!validateEmail(trimmed)) {
          onInvalidEmail?.(trimmed)
          // Keep the original value and close edit mode
          setEditingIndex(null)
          return
        }

        // Check for duplicates (excluding current index)
        if (value.some((email, i) => i !== index && email === trimmed)) {
          setEditingIndex(null)
          return
        }

        // Update the email
        const newValue = [...value]
        newValue[index] = trimmed
        onChange(newValue)
        setEditingIndex(null)
      },
      [value, onChange, validateEmail, onInvalidEmail],
    )

    // Handle pasted content with commas or newlines
    React.useEffect(() => {
      if (
        pendingInput.includes(',') ||
        pendingInput.includes('\n') ||
        pendingInput.includes('\r')
      ) {
        const emails = pendingInput
          .split(/[,\n\r]+/)
          .map((e) => e.trim().toLowerCase())
          .filter((e) => e !== '')

        const validEmails: string[] = []
        emails.forEach((email) => {
          if (validateEmail(email) && !value.includes(email)) {
            validEmails.push(email)
          } else if (!validateEmail(email) && email) {
            onInvalidEmail?.(email)
          }
        })

        if (validEmails.length > 0) {
          onChange([...value, ...validEmails])
        }
        setPendingInput('')
      }
    }, [pendingInput, onChange, value, validateEmail, onInvalidEmail])

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
          e.preventDefault()
          addEmail(pendingInput)
        } else if (
          e.key === 'Backspace' &&
          pendingInput.length === 0 &&
          value.length > 0
        ) {
          e.preventDefault()
          onChange(value.slice(0, -1))
        }
      },
      [pendingInput, value, addEmail, onChange],
    )

    const handleContainerClick = React.useCallback(() => {
      if (editingIndex === null) {
        inputRef.current?.focus()
      }
    }, [editingIndex])

    const handleBlur = React.useCallback(() => {
      if (pendingInput.trim()) {
        addEmail(pendingInput)
      }
    }, [pendingInput, addEmail])

    const handlePaste = React.useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedText = e.clipboardData.getData('text')

        // Check if pasted content has multiple emails (commas, newlines, or spaces)
        if (
          pastedText.includes(',') ||
          pastedText.includes('\n') ||
          pastedText.includes('\r') ||
          pastedText.includes(' ')
        ) {
          e.preventDefault()

          const emails = pastedText
            .split(/[,\n\r\s]+/)
            .map((email) => email.trim().toLowerCase())
            .filter((email) => email !== '')

          const validEmails: string[] = []
          emails.forEach((email) => {
            if (
              validateEmail(email) &&
              !value.includes(email) &&
              !validEmails.includes(email)
            ) {
              validEmails.push(email)
            } else if (!validateEmail(email) && email) {
              onInvalidEmail?.(email)
            }
          })

          if (validEmails.length > 0) {
            onChange([...value, ...validEmails])
          }
        }
      },
      [value, onChange, validateEmail, onInvalidEmail],
    )

    const handleStartEdit = React.useCallback((index: number) => {
      setEditingIndex(index)
    }, [])

    const handleFinishEdit = React.useCallback(
      (index: number, newValue: string) => {
        updateEmail(index, newValue)
      },
      [updateEmail],
    )

    const handleCancelEdit = React.useCallback(() => {
      setEditingIndex(null)
    }, [])

    // Calculate min height based on rows (each row ~28px: 20px line-height + 8px gap)
    const minHeight = minRows * 28 + 20 // Add padding

    return (
      <div
        onClick={handleContainerClick}
        style={{ minHeight }}
        className={cn(
          'flex w-full flex-wrap gap-[10px] rounded-[10px] border-[1.5px] border-solid bg-white p-[10px] text-sm cursor-text content-start',
          'has-[:focus-visible]:border-violet-500',
          invalid
            ? 'border-destructive'
            : 'border-slate-300 hover:border-slate-400',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        {value.map((email, index) => (
          <EditableTag
            key={`${email}-${index}`}
            email={email}
            index={index}
            isEditing={editingIndex === index}
            disabled={disabled}
            onStartEdit={handleStartEdit}
            onFinishEdit={handleFinishEdit}
            onCancelEdit={handleCancelEdit}
            onRemove={removeEmail}
          />
        ))}
        <input
          ref={inputRef}
          type='email'
          className={cn(
            'flex-1 min-w-[150px] outline-none bg-transparent',
            'text-[12px] text-slate-700 placeholder:text-slate-400',
          )}
          value={pendingInput}
          onChange={(e) => setPendingInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          {...props}
        />
      </div>
    )
  },
)

InputEmailTags.displayName = 'InputEmailTags'

export { InputEmailTags }
